'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import InlineGuessReveal from '@/app/components/blog/InlineGuessReveal'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="The EPA Just Listed Microplastics — and Declined to Measure Them"
      author="MicroPlastics Research Desk"
      date="August 18, 2026"
      readTime="12 min read"
      heroImage="/blog/heroes/microplastics-water-filters-compared.webp"
      heroImageAlt="EPA draft Contaminant Candidate List 6 adds microplastics to US drinking water regulation for the first time, while UCMR 6 declines to require monitoring"
    >
      <BlogLastUpdated iso="2026-08-18" formatted="August 18, 2026" />

      <BlogTLDR>
        <strong>On 6 April 2026 the EPA put microplastics on its draft
        Contaminant Candidate List for the first time in the list&rsquo;s
        thirty-year history. Three months later it declined to include them in
        the rule that would actually require anyone to measure them.</strong>{' '}
        Both facts are real and they are usually reported separately. CCL 6 is
        the first formal step under the Safe Drinking Water Act — it identifies
        contaminants that <em>may</em> warrant regulation, and by itself{' '}
        <strong>imposes no requirement on anybody</strong>. UCMR 6, the
        Unregulated Contaminant Monitoring Rule, is the step that makes water
        systems test and report; EPA proposed it for PFAS and others and{' '}
        <strong>left microplastics out</strong>. So the United States has now
        officially acknowledged microplastics in drinking water while creating
        no national dataset about them. Meanwhile HHS launched{' '}
        <strong>STOMP, a $144 million programme</strong> to measure
        microplastics in the human body. Comments on CCL 6 closed 5 June 2026;
        EPA must consult its Science Advisory Board, with{' '}
        <strong>signing targeted for 17 November 2026.</strong>
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>6 April 2026:</strong> EPA released draft CCL 6, and microplastics appear as a priority contaminant group for the first time in the list&rsquo;s 30-year history.</>,
          <><strong>CCL listing creates no obligation.</strong> It is the opening step of the Safe Drinking Water Act process — a signal that EPA is considering whether to regulate, not a decision that it will.</>,
          <><strong>UCMR 6 left microplastics out.</strong> That is the rule that compels water systems to monitor and report, proposed for PFAS and others. Without it there is no national measurement programme.</>,
          <><strong>The result is acknowledgement without data</strong>, which matters because EPA needs occurrence data to justify regulating — and the mechanism that generates it was not switched on.</>,
          <><strong>Key date: 17 November 2026</strong>, the target for signing the final CCL 6 after Science Advisory Board consultation.</>,
          <><strong>HHS launched STOMP, $144 million</strong>, to measure microplastics in the human body and study health effects — the health side is moving faster than the water side.</>,
          <><strong>Nothing changes for your tap today.</strong> No standard, no limit, no monitoring requirement. This is a five-to-ten-year process at its first milestone.</>,
        ]}
      />

      <KeyStats
        heading="The dates and numbers that matter"
        stats={[
          { value: '6 Apr 2026', label: 'draft CCL 6 released', context: 'first time microplastics appear on the Contaminant Candidate List in its 30-year history', source: 'US EPA, Draft Contaminant Candidate List 6', sourceUrl: 'https://www.epa.gov/ccl/draft-contaminant-candidate-list-6-ccl-6' },
          { value: '17 Nov 2026', label: 'target date for signing the final CCL 6', context: 'after Science Advisory Board consultation; comments closed 5 June 2026', source: 'US EPA', sourceUrl: 'https://www.epa.gov/ccl/draft-contaminant-candidate-list-6-ccl-6' },
          { value: 'Excluded', label: 'microplastics from UCMR 6', context: 'the monitoring rule that would require water systems to test and report — proposed for PFAS, not for microplastics', source: 'Holland & Knight, EPA proposes UCMR 6', sourceUrl: 'https://www.hklaw.com/en/insights/publications/2026/07/epa-proposes-ucmr-6-for-pfas-and-other-contaminants' },
          { value: '$144M', label: 'HHS STOMP programme', context: 'Systematic Targeting of MicroPlastics — measuring microplastics in the human body and studying health effects', source: 'US Department of Health and Human Services', sourceUrl: 'https://www.epa.gov/newsreleases/epa-takes-bold-action-ensure-drinking-water-safe-microplastics-pharmaceuticals-and' },
          { value: 'Zero', label: 'enforceable US limits on microplastics in drinking water', context: 'unchanged by any of the above — CCL listing is a candidacy, not a standard', source: 'US EPA, Safe Drinking Water Act national primary drinking water regulations', sourceUrl: 'https://www.epa.gov/ccl/draft-contaminant-candidate-list-6-ccl-6' },
        ]}
      />

      <h2>What a Contaminant Candidate List actually is</h2>
      <p>
        The Safe Drinking Water Act requires the EPA to publish, every five
        years, a list of contaminants that are{' '}
        <strong>not currently regulated</strong>, are known or anticipated to
        occur in public water systems, and{' '}
        <strong>may require regulation in future</strong>. That is the
        Contaminant Candidate List, and it is deliberately the beginning of a
        process rather than the end of one.
      </p>
      <p>
        Being listed means EPA has decided a substance is worth studying with a
        view to possible regulation. It does not mean a limit exists, that
        anyone must test for it, or that any water system must do anything
        differently.{' '}
        <strong>A CCL entry is a candidacy.</strong>
      </p>
      <p>
        That is why the April announcement was genuinely significant without
        being immediately consequential.{' '}
        <strong>Thirty years of CCLs had never included microplastics.</strong>{' '}
        Their appearance on CCL 6 is the first time the US federal drinking
        water apparatus has formally acknowledged plastic particles as a
        candidate contaminant at all.
      </p>

      <h2>The part that got reported separately</h2>
      <p>
        Here is the tension, and it is the reason this article exists.
      </p>
      <p>
        Running alongside the CCL is a different instrument: the{' '}
        <strong>Unregulated Contaminant Monitoring Rule</strong>. Where the CCL
        says <em>this might need regulating</em>, the UCMR says{' '}
        <em>go and measure it</em>. It compels public water systems to sample
        for specified contaminants and report the results, which is how EPA
        builds the national occurrence dataset it needs before it can justify a
        standard.
      </p>
      <p>
        In July 2026 EPA proposed UCMR 6, covering PFAS and other contaminants.{' '}
        <strong>Microplastics were not included.</strong>
      </p>
      <p>
        Put the two together and the shape of the year becomes clear.{' '}
        <strong>Microplastics are now officially a candidate for regulation,
        and there is still no mechanism generating the national data that
        regulation would require.</strong> That is not necessarily bad faith —
        there is a genuine methodological obstacle, which is that the analytical
        methods are not standardised enough to hand to thousands of water
        systems and expect comparable numbers back. But the practical effect is
        a listing that cannot easily progress, because the evidence needed to
        advance it is not being systematically collected.
      </p>

      <InlineGuessReveal
        source="epa_ccl6_vs_ucmr6"
        question="Microplastics were added to the EPA's draft CCL 6. What does that require water utilities to do?"
        options={[
          { label: 'Test for microplastics annually' },
          { label: 'Meet a new federal limit' },
          { label: 'Nothing at all', correct: true },
          { label: 'Notify customers if detected' },
        ]}
        answer="Nothing — a CCL listing creates no obligation"
        answerNote={
          <>
            The Contaminant Candidate List identifies substances that are
            unregulated but might warrant regulation later. It sets no limit
            and triggers no testing. The instrument that compels monitoring is
            the Unregulated Contaminant Monitoring Rule — and when EPA proposed
            UCMR 6 in July 2026, microplastics were left out. So the listing is
            real and the measurement is not.
          </>
        }
        ctaText="No national standard means the filter choice is still yours."
        ctaLabel="Check your water setup"
      />

      <RiskQuizInlineNudge source="blog_inline_epa_ccl6" />

      <h2>Two instruments, easily confused</h2>

      <BlogComparisonTable
        caption="CCL 6 and UCMR 6 compared — why one is a headline and the other is the mechanism (August 2026)"
        columns={[
          { key: 'x', label: '' },
          { key: 'ccl', label: 'CCL 6' },
          { key: 'ucmr', label: 'UCMR 6' },
        ]}
        rows={[
          { x: 'What it does', ccl: 'Lists contaminants that may warrant regulation', ucmr: 'Requires water systems to sample and report' },
          { x: 'Microplastics included?', ccl: 'Yes — first time in 30 years', ucmr: 'No' },
          { x: 'Creates an obligation?', ccl: 'No', ucmr: 'Yes, for the systems covered' },
          { x: 'Sets a limit?', ccl: 'No', ucmr: 'No — it generates data toward one' },
          { x: 'Status', ccl: 'Draft; signing targeted 17 Nov 2026', ucmr: 'Proposed July 2026' },
          { x: 'Practical effect today', ccl: 'Signal of intent', ucmr: 'The step that would produce national data' },
        ]}
      />

      <p>
        The distinction matters for reading coverage. Headlines in April said
        the EPA was &ldquo;taking action&rdquo; on microplastics in drinking
        water, which is fair as far as it goes. Coverage in July reported a
        monitoring rule that omitted them, which is also fair.{' '}
        <strong>Almost nothing put the two side by side</strong>, and side by
        side is the only way the year makes sense.
      </p>

      <h2>How this compares with Europe</h2>
      <p>
        The contrast is instructive, because the two jurisdictions are solving
        different halves of the problem.
      </p>
      <p>
        The EU moved first on <strong>intentionally added</strong>{' '}
        microplastics — Regulation (EU) 2023/2055 restricts synthetic polymer
        microparticles deliberately put into products, with staggered deadlines
        running from 2023 to 2035. It is enforceable, dated, and specific about
        product categories. What it does not do is set a limit for microplastics{' '}
        <em>in drinking water</em>. See{' '}
        <Link href="/blog/eu-microplastics-ban" className="text-forest underline">the EU microplastics ban explained</Link>.
      </p>
      <p>
        The US has now started at the other end — the unintentional particles
        that end up in water supplies — but at the earliest possible stage of
        the process and without the monitoring instrument attached.{' '}
        <strong>Neither jurisdiction currently has an enforceable limit for
        microplastics in tap water.</strong> California remains the only place
        to have built testing requirements for drinking water microplastics,
        and it is a state programme rather than a federal one.
      </p>

      <BlogAppCTA
        headline="Regulation is years away. Your kitchen is not."
        sub="No federal standard exists and none is imminent, so the decisions that change your exposure are still the ones you make at home. Scan any product with MicroPlastics for a 0–100 score."
      />

      <h2>What this means for you, honestly</h2>
      <ul>
        <li>
          <strong>Nothing changes at your tap today.</strong> No limit, no
          required testing, no notification. Anyone telling you the EPA has
          &ldquo;regulated&rdquo; microplastics is a step or three ahead of the
          record.
        </li>
        <li>
          <strong>Do not wait for a standard before deciding anything.</strong>{' '}
          CCL listing to enforceable limit has historically taken many years,
          and this one is missing its data-collection stage. PFAS was on CCL
          lists for well over a decade before limits arrived.
        </li>
        <li>
          <strong>Watch 17 November 2026.</strong> If microplastics survive
          Science Advisory Board consultation into the final CCL 6, the listing
          is durable. If they are dropped, the process resets for another five
          years.
        </li>
        <li>
          <strong>The real signal to watch is a future UCMR.</strong> Listing
          is rhetoric; monitoring is infrastructure. Microplastics appearing in
          a monitoring rule would be the genuinely consequential moment.
        </li>
        <li>
          <strong>Filtration remains the only lever you control.</strong> With
          no standard to comply with, the practical question is unchanged —
          which filter, and does it work. See{' '}
          <Link href="/blog/microplastics-water-filters-compared" className="text-forest underline">water filters compared</Link>{' '}
          and{' '}
          <Link href="/blog/microplastic-testing" className="text-forest underline">what a lab test can actually measure</Link>.
        </li>
        <li>
          <strong>Expect the health data to arrive first.</strong> HHS&rsquo;s
          $144 million STOMP programme is measuring microplastics in human
          tissue while the water side is still deciding whether to count them
          in water. That inversion — knowing what is in people before knowing
          what is in the supply — is likely to shape the next few years of
          coverage.
        </li>
      </ul>

      <p>
        Related:{' '}
        <Link href="/blog/microplastics-in-tap-water" className="text-forest underline">microplastics in tap water</Link>,{' '}
        <Link href="/blog/eu-microplastics-ban" className="text-forest underline">the EU microplastics ban</Link>,{' '}
        <Link href="/blog/do-brita-filters-remove-microplastics" className="text-forest underline">do Brita filters remove microplastics</Link>, and{' '}
        <Link href="/blog/microplastics-in-human-blood" className="text-forest underline">microplastics in human blood</Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Did the EPA regulate microplastics in drinking water?',
            answer: 'No. On 6 April 2026 the EPA added microplastics to its draft Sixth Contaminant Candidate List, which identifies unregulated contaminants that may warrant regulation in future. A CCL listing sets no limit, requires no testing and imposes no obligation on water systems. It is the first step of the Safe Drinking Water Act process, and it was the first time microplastics had appeared on a CCL in the list’s 30-year history.',
          },
          {
            question: 'What is the difference between CCL 6 and UCMR 6?',
            answer: 'The Contaminant Candidate List identifies substances EPA may consider regulating; it creates no obligations. The Unregulated Contaminant Monitoring Rule requires public water systems to sample for specified contaminants and report results, which is how EPA builds the national occurrence data needed to justify a standard. Microplastics were included on draft CCL 6 in April 2026 but were not included when EPA proposed UCMR 6 in July 2026 — so they are listed as a candidate without a mechanism generating national data about them.',
          },
          {
            question: 'When will the EPA decide on microplastics?',
            answer: 'The immediate milestone is the final CCL 6. Public comments on the draft closed on 5 June 2026, EPA must consult its Science Advisory Board, and signing is targeted for 17 November 2026. Surviving into the final list keeps microplastics in the regulatory process; it does not create a standard. Historically the path from CCL listing to an enforceable drinking water limit has taken many years — PFAS appeared on candidate lists well over a decade before limits were set.',
          },
          {
            question: 'Does this mean my tap water is unsafe?',
            answer: 'It is not a safety finding. A CCL listing reflects that a contaminant is unregulated, plausibly present in public water systems, and worth studying — not that a hazard threshold has been crossed. No enforceable federal limit for microplastics in drinking water exists in the United States, and none is imminent. California is the only US jurisdiction to have built drinking water microplastic testing requirements, and that is a state programme.',
          },
          {
            question: 'What is the HHS STOMP programme?',
            answer: 'Systematic Targeting of MicroPlastics, a $144 million initiative launched alongside the EPA listing, aimed at measuring microplastics in the human body and studying the health effects. It is notable mainly for the sequencing it creates: substantial federal money is going into measuring particles in human tissue while the drinking water side has declined, for now, to require measurement of particles in water.',
          },
          {
            question: 'How does the US approach compare with the EU?',
            answer: 'They address different halves of the problem. The EU restricts intentionally added microplastics through Regulation (EU) 2023/2055, with enforceable dates running from 2023 to 2035 by product category — but it sets no drinking water limit. The US has begun at the other end, with unintentional particles in water supplies, though only at the candidate-listing stage and without the monitoring rule attached. Neither currently has an enforceable limit for microplastics in tap water.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'US Environmental Protection Agency', year: 2026, title: 'Draft Contaminant Candidate List 6 (CCL 6) — released 6 April 2026; microplastics listed as a priority contaminant group for the first time; comments closed 5 June 2026; signing targeted 17 November 2026', publisher: 'US EPA', url: 'https://www.epa.gov/ccl/draft-contaminant-candidate-list-6-ccl-6' },
          { authors: 'US Environmental Protection Agency', year: 2026, title: 'EPA takes action to ensure drinking water is safe from microplastics, pharmaceuticals and potential hidden contaminants', publisher: 'US EPA newsroom', url: 'https://www.epa.gov/newsreleases/epa-takes-bold-action-ensure-drinking-water-safe-microplastics-pharmaceuticals-and' },
          { authors: 'Holland & Knight', year: 2026, title: 'EPA proposes UCMR 6 for PFAS and other contaminants, declines adding microplastics — the monitoring rule that would require water systems to sample and report', publisher: 'Holland & Knight Insights', url: 'https://www.hklaw.com/en/insights/publications/2026/07/epa-proposes-ucmr-6-for-pfas-and-other-contaminants' },
          { authors: 'Crowell & Moring LLP', year: 2026, title: 'EPA places microplastics, PFAS, pharmaceuticals and DBPs on draft Sixth Contaminant Candidate List', publisher: 'Crowell & Moring client alert', url: 'https://www.crowell.com/en/insights/client-alerts/epa-places-microplastics-pfas-pharmaceuticals-and-dbps-on-draft-sixth-contaminant-candidate-list' },
          { authors: 'European Commission', year: 2023, title: 'Regulation (EU) 2023/2055 — restriction on intentionally added synthetic polymer microparticles, staggered 2023–2035 by product category', publisher: 'EUR-Lex', url: 'https://eur-lex.europa.eu/eli/reg/2023/2055/oj' },
        ]}
      />
    </BlogPostLayout>
  )
}
